import React from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import {
  SwapHoriz,
  TrendingUp,
  TrendingDown,
  Schedule,
  CheckCircle
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * RebalancePlanCard Component
 * 
 * Displays portfolio rebalancing plan with:
 * - Turnover metrics
 * - Estimated costs
 * - Recommended vs deferred trades
 * - Trade details
 * 
 * Phase 1 Day 9: Rebalance Integration
 * 
 * @param {Object} rebalancePlan - Rebalance plan data from API
 */
const RebalancePlanCard = ({ rebalancePlan }) => {
  const { t } = useTranslation();

  if (!rebalancePlan) return null;

  const {
    is_initial = false,
    summary = {},
    optimized_plan = {}
  } = rebalancePlan;

  const {
    optimized_turnover = 0,
    estimated_cost = 0,
    num_recommended_trades = 0,
    num_deferred_trades = 0,
    num_periods = 1
  } = summary;

  const {
    recommended_trades = [],
    deferred_trades = []
  } = optimized_plan;

  // Helper functions
  const getTurnoverColor = (turnover) => {
    if (turnover < 0.2) return 'success';
    if (turnover < 0.5) return 'warning';
    return 'error';
  };

  const getTurnoverLevel = (turnover) => {
    if (turnover < 0.2) return t('rebalance.levels.turnover.low');
    if (turnover < 0.5) return t('rebalance.levels.turnover.moderate');
    return t('rebalance.levels.turnover.high');
  };

  const getCostColor = (cost) => {
    if (cost < 0.005) return 'success';
    if (cost < 0.01) return 'warning';
    return 'error';
  };

  const getCostLevel = (cost) => {
    if (cost < 0.005) return t('rebalance.levels.cost.low');
    if (cost < 0.01) return t('rebalance.levels.cost.moderate');
    return t('rebalance.levels.cost.high');
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'BUY':
        return <TrendingUp fontSize="small" sx={{ color: 'success.main' }} />;
      case 'SELL':
        return <TrendingDown fontSize="small" sx={{ color: 'error.main' }} />;
      default:
        return <CheckCircle fontSize="small" sx={{ color: 'info.main' }} />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'BUY':
        return 'success';
      case 'SELL':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card sx={{ p: 2.5, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <SwapHoriz sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('rebalance.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {is_initial ? t('rebalance.initialAllocation') : t('rebalance.rebalancing')}
          </Typography>
        </Box>
      </Box>

      {/* Initial Allocation Alert */}
      {is_initial && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.75, fontSize: '0.75rem' }}>
          {t('rebalance.allNew')}
        </Alert>
      )}

      {/* Summary Metrics - 2x2 Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2 }}>
        {/* Turnover */}
        <Tooltip title={t('rebalance.tooltips.turnover')} arrow>
          <Box sx={{ 
            p: 1.5, 
            bgcolor: 'background.default', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              {t('rebalance.metrics.turnover')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {(optimized_turnover * 100).toFixed(1)}%
              </Typography>
              <Chip 
                label={getTurnoverLevel(optimized_turnover)}
                color={getTurnoverColor(optimized_turnover)}
                size="small"
                sx={{ fontSize: '0.6rem', height: 18 }}
              />
            </Box>
          </Box>
        </Tooltip>

        {/* Estimated Cost */}
        <Tooltip title={t('rebalance.tooltips.estimatedCost')} arrow>
          <Box sx={{ 
            p: 1.5, 
            bgcolor: 'background.default', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              {t('rebalance.metrics.estimatedCost')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {(estimated_cost * 100).toFixed(2)}%
              </Typography>
              <Chip 
                label={getCostLevel(estimated_cost)}
                color={getCostColor(estimated_cost)}
                size="small"
                sx={{ fontSize: '0.6rem', height: 18 }}
              />
            </Box>
          </Box>
        </Tooltip>

        {/* Recommended Trades */}
        <Tooltip title={t('rebalance.tooltips.recommendedTrades')} arrow>
          <Box sx={{ 
            p: 1.5, 
            bgcolor: 'background.default', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              {t('rebalance.metrics.recommendedTrades')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'success.main' }}>
              {num_recommended_trades}
            </Typography>
          </Box>
        </Tooltip>

        {/* Deferred Trades or Periods */}
        {num_deferred_trades > 0 ? (
          <Tooltip title={t('rebalance.tooltips.deferredTrades')} arrow>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'background.default', 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                {t('rebalance.metrics.deferredTrades')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'warning.main' }}>
                {num_deferred_trades}
              </Typography>
            </Box>
          </Tooltip>
        ) : num_periods > 1 ? (
          <Tooltip title={t('rebalance.tooltips.periods')} arrow>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'background.default', 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                {t('rebalance.metrics.periods')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {num_periods}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{ 
            p: 1.5, 
            bgcolor: 'success.lighter', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600, fontSize: '0.75rem' }}>
              ✓ {t('rebalance.noTrades')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Trade Details */}
      {recommended_trades && recommended_trades.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>
            {t('rebalance.trades.title')} - {t('rebalance.trades.recommended')}
          </Typography>
          <TableContainer sx={{ maxHeight: 250, mb: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.ticker')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.action')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.currentWeight')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.targetWeight')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.change')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recommended_trades.map((trade, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>
                      {trade.ticker}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      <Chip
                        icon={getActionIcon(trade.action)}
                        label={t(`rebalance.trades.actions.${trade.action}`)}
                        color={getActionColor(trade.action)}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      {((trade.current_weight || 0) * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      {((trade.target_weight || 0) * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      <Typography 
                        component="span"
                        sx={{ 
                          color: trade.change > 0 ? 'success.main' : 'error.main',
                          fontWeight: 600
                        }}
                      >
                        {trade.change > 0 ? '+' : ''}{((trade.change || 0) * 100).toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Deferred Trades */}
      {deferred_trades && deferred_trades.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem', color: 'warning.main' }}>
            {t('rebalance.trades.title')} - {t('rebalance.trades.deferred')}
          </Typography>
          <Alert severity="warning" sx={{ mb: 1, py: 0.75, fontSize: '0.7rem' }}>
            <Schedule sx={{ fontSize: 16, mr: 0.5 }} />
            {t('rebalance.tooltips.deferredTrades')}
          </Alert>
          <TableContainer sx={{ maxHeight: 180 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.ticker')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.action')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('rebalance.trades.change')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deferred_trades.map((trade, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>
                      {trade.ticker}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      <Chip
                        label={t(`rebalance.trades.actions.${trade.action}`)}
                        color="default"
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                      {((trade.change || 0) * 100).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Card>
  );
};

export default RebalancePlanCard;
